import mixin from 'privates/utils/mixin';
import FormElement from './FormElement';
import DisabledMixin from './mixins/DisabledMixin';
import HiddenCollapsedMixin from './mixins/HiddenCollapsedMixin';
import FocusMixin from './mixins/FocusMixin';
import ClickableMixin from './mixins/ClickableMixin';
import * as Utils from 'ui/Utils';
import {validate} from 'privates/validations/validation';
import * as members from 'privates/privateMembers';
import valueSanitzationAlgorithm from 'privates/utils/valueSanitzationAlgorithm';
import {runValidationsAfterChange} from 'privates/validationsService/validationsService';
import isNil from 'lodash/isNil';



/**
 * @class Switch
 * @summary Switches are used for a single binary choice.
 * @mixes $w.FormElement
 * @mixes $w.DisabledMixin
 * @mixes $w.HiddenCollapsedMixin
 * @mixes $w.FocusMixin
 * @mixes $w.ClickableMixin
 * @memberof $w
 * @tagname switch
 * @viewername wixui.ToggleSwitch
 */
class Switch extends FormElement {
    constructor(RMI, compId, contextId, wixSelector, controllerId, role, type) {
        super(RMI, compId, contextId, wixSelector, controllerId, role, type);
        mixin(Switch.prototype, DisabledMixin.prototype);
        mixin(Switch.prototype, HiddenCollapsedMixin.prototype);
        mixin(Switch.prototype, FocusMixin.prototype);
        mixin(Switch.prototype, ClickableMixin.prototype);
    }
    /**
     * @member checked
     * @label property
     * @syntax
     * get checked(): boolean
     * set checked(value: boolean): void
     * @summary Sets or gets whether a switch is on or off.
     * @description
     *  Setting the `checked` property to `true` toggles the switch to the on
     *  position. Setting it to `false` toggles the switch to the off position.
     *
     *  Getting the `checked` property returns whether the switch is on or off.
     * @type {external:Boolean}
     * @memberof $w.Switch
     * @instance
     */
    get checked () {
        return Utils.getData(this, 'checked') || false;
    }

    /**
     * @param {external:Boolean} checked
     * @ignore
     */
    set checked (checked) {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const compType = members._type.get(this).compType;

        if (!validate(compType, 'checked', {checked})) {
            return;
        }

        runValidationsAfterChange(RMI, compId, checked);
        if (isNil(checked)){
            checked = false;
        }
        Utils.setData(this, { checked });
    }

    /**
     * @member value
     * @label property
     * @syntax
     * get value(): string
     * set value(value: string): void
     * @summary Sets or gets a switch's value.
     * @description
     *  The `value` property is not related to whether the switch is on or
     *  off. To determine whether the switch is on or off, use the [`checked`](#checked)
     *  property.
     *
     *  The `value` property is used for storing a value that is associated with
     *  the switch.
     *
     *  It does not trigger an `onChange` event when the value is modified.
     *
     *  Even if a switch is connected to a dataset, its `value` property
     *  is not related to the value of the collection field it is connected to.
     *  The field's value is determined by the switch's [`checked`](#checked) property.
     * @snippet [FormElement-value_get.es6=Get an element's value]
     * @snippet [FormElement-value_set.es6=Set an element's value]
     * @type {external:String}
     * @memberof $w.Switch
     * @override
     */
    get value() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        return RMI.getData(compId).value;
    }

    /**
     * @param {external:String} val The input element's new value.
     * @ignore
     */
    set value(val) {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const compData = RMI.getFullDataWithOverrides(compId);
        const compType = members._type.get(this).compType;
        const value = valueSanitzationAlgorithm(compData, val);
        const isValid = validate(compType, 'value', {value});

        if (!isValid) {
            return;
        }

        RMI.setData(compId, { value });
    }

    toJSON() {
        const {id, type, global, rendered, required, value, checked} = this;
        return {id, type, global, rendered, required, value, checked};
    }
}

export default Switch;



// WEBPACK FOOTER //
// ./js/modules/ui/Switch.es6